import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { LocalizedText } from 'bernie-l10n';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { RoomViewModel } from 'models';

import { classNames } from 'uitk-react-utils';
import { UitkCard, UitkCardContent, UitkCardTextSegment } from 'uitk-react-cards';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';

import { ChooseDatesCTA } from './choose-dates-cta';
import { LargeView } from './large-view';
import { ListingPlaceholderSmall } from 'components/infosite/rooms-and-rates/loading-placeholder';
import { RoomAmenities } from 'components/infosite/rooms-and-rates/room-amenities';
import { RoomDetailsRoomScore } from 'components/infosite/rooms-and-rates/room/internals/room-details-room-score';
import { RoomDetailsRoomSummary } from './room-details-room-summary';
import { RoomInfo } from 'components/infosite/rooms-and-rates/room-info';
import { RoomPhotos } from 'components/infosite/rooms-and-rates/room-photos';
import { RoomRateExpandableOptions } from 'components/infosite/rooms-and-rates/room-rate-expand-collapse';
import { RoomRateOptions } from 'components/infosite/rooms-and-rates/room-rate-options';

export interface RoomProps {
  analytics?: AnalyticsStore;
  classList?: string[];
  currentHotel?: CurrentHotelStore;
  isDateless?: boolean;
  loading: boolean;
  position?: string;
  room: RoomViewModel;
  routeStore?: RouteStore;
}

export interface RoomState {
  initialized: boolean;
}

export class Room extends React.Component<RoomProps, RoomState> {
  constructor(props: RoomProps) {
    super(props);

    this.state = { initialized: false };
  }

  public componentDidMount() {
    this.setState({ initialized: true });
  }

  public render() {
    return (
      <Viewport>
        <ViewSmall>
          <DefaultView {...this.props} initialized={this.state.initialized} />
        </ViewSmall>
        <ViewLarge>
          <LargeView {...this.props} />
        </ViewLarge>
      </Viewport>
    );
  }
}

interface DefaultViewProps extends RoomProps {
  initialized: boolean;
}

export const DefaultView = ({ classList, currentHotel, loading, position, room, isDateless, routeStore, analytics }: DefaultViewProps) => {
  const { photos, roomTypeCode, roomSummary, name } = room;

  if (loading) {
    return <ListingPlaceholderSmall />;
  }

  const roomScoreInfo = currentHotel.getRoomScore(roomTypeCode);

  const roomNameClass = classNames('room_name', { 'all-b-padding-two': !roomSummary });
  const rateOptionsProps = {
    room,
    position,
    id: 'infosite-room-ratePlans',
  };

  return (
    <li className={`uitk-cell room ${classList?.join(' ')}`} data-stid="section-roomtype">
      <h3 className="is-visually-hidden">{name}</h3>
      <UitkCard className="room__card">
        {photos && photos.length > 0 && <RoomPhotos position={position} name={name} room={room} />}
        <UitkCardContent>
          <UitkCardTextSegment className="uitk-card-separator-bottom">
            <div className={roomNameClass} data-stid="content-room-name">
              <RoomName room={room} />
            </div>
            {roomScoreInfo && <RoomDetailsRoomScore roomScore={roomScoreInfo.score} />}
            <RoomDetailsRoomSummary roomSummary={roomSummary} />
            <RAmenities room={room} />
            <InfoDialogLink
              className="room__more-details"
              content={
                <span className="uitk-type-300 uitk-type-right all-t-padding-four infoicon__more uitk-grid all-grid-align-end">
                  <LocalizedText message="hotels.infosite.rateInfo.moreDetails.linkText" />
                  <UitkIcon
                    className="all-b-margin-half"
                    name="navigate_next"
                    size={UitkIconSize.SMALL}
                    description={name}
                    id={`room-see-more-details-${position}`}
                  />
                </span>
              }
              dataStid="content-room-more-details"
              loading={loading}
              position={position}
              room={room}
              isDateless={isDateless}
            />
          </UitkCardTextSegment>

          {isDateless && (
            <UitkCardTextSegment data-stid="choose-date-cta-segment">
              <ChooseDatesCTA hotelPosition={position} routeStore={routeStore} />
            </UitkCardTextSegment>
          )}
          <Viewport>
            <ViewSmall>
              <RoomRateExpandableOptions {...rateOptionsProps} ratePlanCollection={room.ratePlans} analytics={analytics} />
            </ViewSmall>
            <ViewMedium>
              <RoomRateOptions {...rateOptionsProps} ratePlanCollection={room.ratePlans} />
            </ViewMedium>
          </Viewport>
          <RoomRateOptions
            id="infosite-room-unavailableRatePlans"
            isUnavailable={true}
            position={position}
            ratePlanCollection={room.unavailableRatePlans}
            room={room}
          />
        </UitkCardContent>
      </UitkCard>
    </li>
  );
};

interface InfoDialogLinkProps extends RoomProps {
  className: string;
  content: string | JSX.Element;
  dataStid: string;
}

const InfoDialogLink = ({ className, content, dataStid, position, room, isDateless }: InfoDialogLinkProps) => (
  <RoomInfo
    isDateless={isDateless}
    className={className}
    data-stid={dataStid}
    position={position}
    room={room}
    trackingRfrr={`.RoomInfo${position}`}
    width="full"
  >
    {content}
  </RoomInfo>
);

interface RAmenitiesProps {
  room: RoomViewModel;
}

const RAmenities = ({ room }: RAmenitiesProps) => {
  const { roomAmenities } = room;

  return (
    <div data-stid="content-room-amenities-grid">
      {roomAmenities && roomAmenities.length > 0 && (
        <RoomAmenities className="uitk-grid amenities__list-items all-grid-align-center" roomAmenities={roomAmenities} />
      )}
    </div>
  );
};

interface RoomNameProps {
  icon?: string | JSX.Element;
  room: RoomViewModel;
}

export const RoomName = ({ icon, room }: RoomNameProps) => {
  const { name } = room;

  const roomNameClasses = classNames('uitk-cell', 's-cell-fill', 'm-cell-fill', 'l-cell-shrink', 'uitk-type-heading-500', 'truncate');

  return (
    <span className="uitk-grid all-grid-align-middle">
      <span aria-hidden="true" className={roomNameClasses} data-stid="room-info-title-heading">
        {name}
      </span>
      <span className="is-visually-hidden">
        <LocalizedText message="hotels.infosite.roomInfo.name.allyLabel" data={[name]} />
      </span>
      {icon}
    </span>
  );
};
